import { FunctionComponent } from "react";

import { faBolt } from "@fortawesome/pro-solid-svg-icons";

import { useTranslation } from "react-i18next";

import Checked from "@assets/icons/checked.svg?url";
import Refresh from "@assets/icons/refresh.svg?url";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { AiSnippetStatus } from "../utils/getAiSnippetStatus";

const TOOLTIP_KEYS: Record<AiSnippetStatus, string> = {
	not_created: "messages.aiSnippetPending",
	creating: "messages.aiSnippetGenerating",
	created: "messages.aiSnippetReady",
};

/** Border + tinted background that match the neighbouring tags (InfoTag/CampaignTag) per status. */
const CONTAINER_STYLES: Record<AiSnippetStatus, string> = {
	not_created: "border-blue-action/50 bg-blue-action/10",
	creating: "border-blue-action/50 bg-blue-action/10",
	created: "border-status-success/50 bg-status-success-light/50",
};

/** Base glyph colour follows the status family, like the icons on the neighbouring tags. */
const ICON_COLORS: Record<AiSnippetStatus, string> = {
	not_created: "text-blue-action",
	creating: "text-blue-action",
	created: "text-status-success",
};

/**
 * Status indicator shown next to the campaign label when the assigned campaign uses AI snippets.
 * Rendered as a bordered chip (matching the neighbouring tags) holding an AI glyph with a corner
 * status badge — blue refresh = not yet created, spinning blue refresh = creating, green check = created.
 */
export const AiSnippetStatusIcon: FunctionComponent<{ status: AiSnippetStatus }> = ({ status }) => {
	const { t } = useTranslation();

	return (
		<Tooltip content={t(TOOLTIP_KEYS[status])}>
			<span
				className={classNames(
					"mb-1 inline-flex items-center justify-center rounded-md border px-2 py-1 transition-colors",
					CONTAINER_STYLES[status]
				)}
			>
				<span className="relative inline-flex h-[20px] w-[20px] items-center justify-center">
					{status === "created" && (
						<img alt="created" className="absolute right-[-1px] top-[-1px] h-3 w-3" src={Checked} />
					)}
					{status === "not_created" && (
						<img
							alt="not created"
							className="absolute right-[-1px] top-[-1px] h-3 w-3"
							src={Refresh}
						/>
					)}
					{status === "creating" && (
						<img
							alt="creating"
							className="absolute right-[-1px] top-[-1px] h-3 w-3 animate-spin"
							src={Refresh}
						/>
					)}
					<Icon className={classNames("h-4 w-4", ICON_COLORS[status])} icon={faBolt} />
				</span>
			</span>
		</Tooltip>
	);
};
